import React, { FC, FunctionComponent, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { fetchQuery, graphql, useLazyLoadQuery, useRelayEnvironment } from 'react-relay';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { T, toLocalID } from 'src/shared/helpers';
import CloseIcon from 'src/shared/material_icons/navigation/close.svg';
import VisibilityIcon from 'src/shared/material_icons/action/visibility.svg';
import VisibilityOffIcon from 'src/shared/material_icons/action/visibility_off.svg';

import { useRelayStore } from 'src/relay/environment';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import styled from 'styled-components';
import { PrimaryButton } from '../../widgets/buttons';
import { FullscreenLayout } from '../../widgets/fullscreenIframeLayout';
import { Icon } from '../../widgets/icon';
import { ZTooltip } from '../../widgets/ztooltip';
import { RowActionButton } from 'src/shared/globals';
import PlacementPage from '../placement/placement';
import { PlacementEditorHandle } from '../placement/placementAreaDesigner';
import { placementsFetchProductNameQuery } from './__generated__/placementsFetchProductNameQuery.graphql';
import { placementsQuery } from './__generated__/placementsQuery.graphql';
import { PlacementPageSkeleton } from './placementSkeleton';

const EyeButtonIcon = styled(Icon)``;

const fetchProductNameQuery = graphql`
    query placementsFetchProductNameQuery($id: ID!) {
        model(id: $id) {
            id
            name
        }
    }
`;

export const EyeButton: FunctionComponent<{ enabled: boolean; onClick: () => void }> = ({ enabled, onClick }) => {
    const EyeIcon = enabled ? VisibilityIcon : VisibilityOffIcon;

    return (
        <RowActionButton onClick={onClick}>
            <EyeButtonIcon>
                <EyeIcon />
            </EyeButtonIcon>
        </RowActionButton>
    );
};

// Fullscreen layout

const FullscreenOverlay = styled(FullscreenLayout)`
    overflow: hidden;
`;

const TopBar = styled.div`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding: 12px 24px;
    background: white;
    border-bottom: 1px solid #e2e8f0;
    min-height: 64px;
`;

const TopBarLeft = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const TopBarCenter = styled.div`
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
`;

const TopBarCenterTitle = styled.span`
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
`;

const TopBarCenterLink = styled.a`
    font-size: 11px;
    color: #3b82f6;
    text-decoration: none;
    &:hover {
        color: #2563eb;
    }
`;

const ProductNameLabel = styled.span`
    font-size: 12px;
    color: #64748b;
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding-left: 8px;
`;

const TopBarRight = styled.div`
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 16px;
`;

const EditableTitle = styled.input`
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
    border: none;
    background: transparent;
    outline: none;
    padding: 4px 8px;
    border-radius: 6px;
    min-width: 150px;
    max-width: 400px;

    &:hover {
        background: #f3f5f6;
    }

    &:focus {
        background: #f3f5f6;
        box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
    }
`;

const CloseButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: #000000;
    color: #ffffff;
    cursor: pointer;
    transition: background 0.2s ease;
    flex-shrink: 0;

    &:hover {
        background: #333333;
    }

    ${Icon} {
        width: 16px;
        height: 16px;
        min-width: 0;
        fill: currentColor;
    }
`;

const SaveCloseGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const ContentArea = styled.div`
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding: 8px;
`;

const ContentContainer = styled.div`
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    padding: 10px 15px;
`;

// Content component

const PlacementsContent: FC<{ modelId: string; productName: string; placementIdParam?: string; isNew?: boolean }> = ({
    modelId,
    productName: urlProductName,
    placementIdParam,
    isNew
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const fromSidebar = !!(location.state as { fromSidebar?: boolean } | null)?.fromSidebar;
    const environment = useRelayEnvironment();
    const { subscription } = useSubscriptionData();
    const saveRef = useRef<(() => void) | null>(null);
    const editorRef = useRef<PlacementEditorHandle | null>(null);
    const [savingInProgress, setSavingInProgress] = useState(false);
    const wasSavingRef = useRef(false);
    const saveInitiatedRef = useRef(false);
    const [editableName, setEditableName] = useState<string>('');
    const [canSave, setCanSave] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [resolvedProductName, setResolvedProductName] = useState(urlProductName);

    useEffect(() => {
        if (urlProductName) return;
        fetchQuery<placementsFetchProductNameQuery>(environment, fetchProductNameQuery, { id: modelId })
            .toPromise()
            .then((result) => {
                if (result?.model?.name) {
                    setResolvedProductName(result.model.name);
                }
            });
    }, [modelId, urlProductName, environment]);

    const data = useLazyLoadQuery<placementsQuery>(
        graphql`
            query placementsQuery($id: ID!) {
                placementsOutput(id: $id) {
                    id
                    modelID
                    isEnabled
                    name
                }
            }
        `,
        { id: `guid://Zakeke/Placements/${modelId}` },
        { fetchPolicy: 'network-only' }
    );

    const resolvedPlacement = (() => {
        if (isNew) return null;
        const placements = data.placementsOutput ?? [];
        if (placementIdParam) {
            const paramNum = parseInt(placementIdParam);
            return placements.find((p) => toLocalID(p.id) === paramNum) ?? null;
        }
        return placements.length > 0 ? placements[0] : null;
    })();
    const initialPlacementIdRef = useRef(resolvedPlacement ? toLocalID(resolvedPlacement.id) : undefined);
    const stablePlacementId = isNew ? undefined : placementIdParam && !resolvedPlacement ? undefined : initialPlacementIdRef.current;

    // Poll the editor ref to sync state from child
    useEffect(() => {
        const interval = setInterval(() => {
            const currentName = editorRef.current?.name;
            if (currentName && currentName !== editableName) {
                setEditableName(currentName);
            }
            const currentCanSave = editorRef.current?.canSave ?? false;
            if (currentCanSave !== canSave) {
                setCanSave(currentCanSave);
            }
            const currentIsDirty = editorRef.current?.isDirty ?? false;
            if (currentIsDirty !== isDirty) {
                setIsDirty(currentIsDirty);
            }
        }, 500);
        return () => clearInterval(interval);
    }, [editableName, canSave, isDirty]);

    const [invalidateRelayStore] = useRelayStore((state) => [state.invalidateRelayStore]);

    const handleClose = () => {
        invalidateRelayStore();
        navigate(-1);
    };

    const handleSave = () => {
        const saveFn = editorRef.current?.save ?? saveRef.current;
        if (saveFn) {
            saveInitiatedRef.current = true;
            saveFn();
        }
    };

    useEffect(() => {
        if (wasSavingRef.current && !savingInProgress && saveInitiatedRef.current && fromSidebar) {
            saveInitiatedRef.current = false;
            invalidateRelayStore();
            navigate(-1);
        }
        wasSavingRef.current = savingInProgress;
    }, [savingInProgress, fromSidebar, navigate, invalidateRelayStore]);

    const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const newName = e.currentTarget.value;
        setEditableName(newName);
        editorRef.current?.setName(newName);
    }, []);

    const handleNameFocus = useCallback(() => {
        const currentName = editorRef.current?.name ?? '';
        setEditableName(currentName);
    }, []);

    return (
        <FullscreenOverlay>
            <TopBar>
                <TopBarLeft>
                    <EditableTitle
                        value={editableName || editorRef.current?.name || 'Advanced Print File'}
                        onChange={handleNameChange}
                        onFocus={handleNameFocus}
                    />
                    {resolvedProductName && <ProductNameLabel>{resolvedProductName}</ProductNameLabel>}
                </TopBarLeft>

                <TopBarCenter>
                    <TopBarCenterTitle>{T._('Advanced Print File Builder', 'Admin')}</TopBarCenterTitle>
                    <TopBarCenterLink
                        href='https://zakeke.zendesk.com/hc/en-us/articles/9686184040348-Advanced-Print-Files-Builder#h_01H63Y6GV47Q29JK8K8TA441W9'
                        target='_blank'
                        rel='noopener noreferrer'
                    >
                        {T._('How to use', 'Admin')}
                    </TopBarCenterLink>
                </TopBarCenter>

                <TopBarRight>
                    <SaveCloseGroup>
                        <ZTooltip
                            title={
                                !subscription?.plan?.additionalFeatures.advancedPrintFilesBuilder
                                    ? T._('Upgrade your plan to enable this feature', 'Admin')
                                    : undefined
                            }
                            addContainer
                        >
                            <PrimaryButton disabled={savingInProgress || !canSave || !isDirty} onClick={handleSave}>
                                {T._('Save', 'Admin')}
                            </PrimaryButton>
                        </ZTooltip>
                        <CloseButton onClick={handleClose}>
                            <Icon>
                                <CloseIcon />
                            </Icon>
                        </CloseButton>
                    </SaveCloseGroup>
                </TopBarRight>
            </TopBar>

            <ContentArea>
                <ContentContainer>
                    <PlacementPage
                        modelId={parseInt(modelId)}
                        placementId={stablePlacementId}
                        saveRef={saveRef}
                        onSavingChange={setSavingInProgress}
                        editorRef={editorRef}
                    />
                </ContentContainer>
            </ContentArea>
        </FullscreenOverlay>
    );
};

// Page component

const Placements: FC<{}> = () => {
    const { modelId } = useParams();
    const [searchParams] = useSearchParams();
    const productName = searchParams.get('name') ?? '';
    const placementIdParam = searchParams.get('placementId') ?? undefined;
    const isNew = searchParams.get('new') === '1';

    // Hide Zendesk widget while builder is open (including during loading)
    useEffect(() => {
        const zE = (window as any).zE;
        if (zE) {
            zE('webWidget', 'hide');
        }
        return () => {
            const zE = (window as any).zE;
            if (zE) {
                zE('webWidget', 'show');
            }
        };
    }, []);

    if (!modelId) {
        throw new Error(`Invalid model id ${modelId}`);
    }

    return (
        <Suspense fallback={<PlacementPageSkeleton />}>
            <PlacementsContent modelId={modelId} productName={productName} placementIdParam={isNew ? undefined : placementIdParam} isNew={isNew} />
        </Suspense>
    );
};

export default Placements;
